/**
 * ContactsPage (v1).
 *
 * Operations → Contacts surface, served when the user is on the legacy
 * AppLayout (layout_v2 flag === 'v1'). Mirrors the v2 page in features
 * but uses the v1 PageHeader / shadcn Table / EmptyState chrome.
 *
 * The Add Contact dialog is shared with the v2 page — see
 * src/components/v2/dialogs/AddContactDialog.tsx for the form.
 */
import { useEffect, useState, useMemo } from 'react';
import { Users, Plus, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { TableSkeleton } from '@/components/LoadingSkeleton';
import { AddContactDialog } from '@/components/v2/dialogs/AddContactDialog';
import { authFetch } from '@/lib/auth';
import type { Contact, Tag } from '@shared/types';

export function ContactsPage(): JSX.Element {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const [contactsRes, tagsRes] = await Promise.all([
        authFetch<{ items: Contact[] }>('/api/contacts'),
        authFetch<{ items: Tag[] }>('/api/docs/taxonomy/tags').catch(() => ({ items: [] as Tag[] })),
      ]);
      setContacts(contactsRes.items ?? []);
      setTags(tagsRes.items ?? []);
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to load contacts';
      setError(msg);
      // Match the [Module] convention used by other v1 pages so the
      // server-side client-error log can group these.
      console.error('[Contacts]', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const tagsById = useMemo(() => {
    const m = new Map<string, Tag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);

  const filtered = useMemo<Contact[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const hay = `${c.company} ${c.first_name ?? ''} ${c.last_name ?? ''} ${c.email ?? ''} ${c.phone ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, search]);

  return (
    <>
      <PageHeader
        title="Contacts"
        breadcrumbs={[{ label: 'Dashboard', href: '/' }, { label: 'Contacts' }]}
      >
        <AddContactDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onAdded={fetchData}
          trigger={
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          }
        />
      </PageHeader>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, company, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {loading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={search ? 'No matches' : 'No contacts yet'}
          description={
            search
              ? 'Try a different search term.'
              : 'Add the people and companies your ranch works with — vets, suppliers, the county clerk.'
          }
          actionLabel={!search ? 'Add Contact' : undefined}
          onAction={!search ? () => setDialogOpen(true) : undefined}
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Tags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
                return (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{fullName || '—'}</TableCell>
                    <TableCell>{c.company}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.email ? (
                        <a href={`mailto:${c.email}`} className="hover:underline">
                          {c.email}
                        </a>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.phone || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {c.tag_ids.length === 0 ? (
                          <span className="text-2xs text-muted-foreground">—</span>
                        ) : (
                          c.tag_ids.map((id) => {
                            const tag = tagsById.get(id);
                            return (
                              <Badge key={id} variant="outline" className="text-2xs">
                                {tag?.name ?? id}
                              </Badge>
                            );
                          })
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
